import React, { useState, useEffect } from "react";
import { Flex, Heading, Button, Spinner } from "@chakra-ui/react";
import { textSecondary, primary } from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { getMonthlyTreatmentPlanStatistics } from "../../../../actions/statisticActions.js";
import { useDispatch, useSelector } from "react-redux";
import { subMonths, format } from "date-fns";
// import LineChartNew from "../../../../components/charts/LineChartNew.js";
import DashboardTopServices from "./DashboardTopServices.js";
import BoxListItemNew from "./BoxListItemNew.js";
import { motion } from "framer-motion";
import useThemeColors from "../../../../hooks/useThemeColors.js";
import { formatNumberWithCommas } from "../../../../utils/formatUtils"; // Adjust the path accordingly
import ServicesLineChart from "./ServicesLineChart.js";
import { useParams } from "react-router-dom";

// Helper function to filter last 6 months
const getLast6Months = (monthlyData) => {
  const today = new Date();
  const sixMonthsAgo = subMonths(today, 5); // Go back 5 months to include the current month
  const formattedSixMonthsAgo = format(sixMonthsAgo, "yyyy-MM");

  // Filter data for last 6 months
  return monthlyData.filter((item) => {
    const itemDate = new Date(item.year, item.month - 1); // month is 1-based
    const formattedItemDate = format(itemDate, "yyyy-MM");
    return formattedItemDate >= formattedSixMonthsAgo;
  });
};

const DashboardServices = ({ currency }) => {
  const { slug } = useParams();
  const { t } = useTranslation();

  // COLORS
  const { whiteMode, textPrimaryMode, backgroundLightMode } = useThemeColors();

  const [totalServices, setTotalServices] = useState(0);
  const [totalCompletedServices, setTotalCompletedServices] = useState(0);
  const [lastSixMonthsData, setLastSixMonthsData] = useState([]);

  // REDUX
  const dispatch = useDispatch();

  const statisticMonthlyTreatmentPlan = useSelector(
    (state) => state.statisticMonthlyTreatmentPlan
  );
  const { loading, error, monthlyServices } = statisticMonthlyTreatmentPlan;

  useEffect(() => {
    dispatch(getMonthlyTreatmentPlanStatistics("", slug));
  }, [dispatch]);

  useEffect(() => {
    // console.log("Monthly services updated:", monthlyServices);
    if (monthlyServices) {
      const totalServices = monthlyServices.reduce(
        (acc, item) => acc + (item.totalPrice || 0), // Use totalPrice instead of totalTreatmentPlans
        0
      );
      setTotalServices(totalServices);

      const totalCompletedServices = monthlyServices.reduce(
        (acc, item) => acc + (item.completedServicesTotalPrice || 0), // Corrected to completedServicesTotalPrice
        0
      );
      setTotalCompletedServices(totalCompletedServices);

      setLastSixMonthsData(getLast6Months(monthlyServices));
    }
  }, [monthlyServices]);

  // const lastSixMonthsData = getLast6Months(monthlyServices);

  // console.log("lastSixMonthsData", lastSixMonthsData);
  // console.log("monthlyServices", monthlyServices);
  // console.log("totalServices", totalServices);
  // console.log("totalCompletedServices", totalCompletedServices);

  // console.log("lastSixMonthsData", lastSixMonthsData);

  if (loading)
    return (
      <Flex
        w="100%"
        bg={whiteMode}
        p="1rem 1.5rem"
        borderRadius="1rem"
        h="567px"
        minH="567px"
        maxH="567px"
        align="center"
        justify="center"
      >
        <Spinner size="lg" color={primary} />
      </Flex>
    );
  if (error) return <div>Error loading data</div>;

  return (
    <Flex
      w="100%"
      bg={whiteMode}
      borderRadius="1rem"
      p="1rem 1.5rem"
      direction="column"
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.2 } }}
    >
      <Flex w="100%" align="center" justify="space-between" minH="2rem">
        <Flex align="center">
          <Heading fontSize="1.125rem" fontWeight="600" color={textPrimaryMode}>
            {t("dashboard.services")}
          </Heading>
        </Flex>

        <Flex align="center" justify="flex-end" gap="0.5rem" w="100%">
          <Button
            size="sm"
            fontSize="0.8rem"
            bg={backgroundLightMode}
            color={textSecondary}
            borderRadius="1rem"
            isDisabled
          >
            {t("dashboard.last6Months")}
          </Button>
        </Flex>
      </Flex>

      <Flex w="100%" h="100%" position="relative" direction="column">
        <Flex w="100%" position="relative">
          <ParentSize>
            {({ width }) => (
              <ServicesLineChart
                data={lastSixMonthsData}
                width={width}
                height={253}
                currency={currency}
              />
            )}
          </ParentSize>
        </Flex>

        <Flex w="100%" direction="column" m="1rem 0 0">
          <BoxListItemNew
            label={t("dashboard.totalServicesAmount")}
            data={formatNumberWithCommas(totalServices.toFixed(2))}
            isPrice={true}
            color={textSecondary}
            noTooltip
            currency={currency}
          />
          <BoxListItemNew
            label={t("dashboard.totalCompletedServicesAmount")}
            data={formatNumberWithCommas(totalCompletedServices.toFixed(2))}
            isPrice={true}
            color={textSecondary}
            noTooltip
            currency={currency}
          />
        </Flex>

        <DashboardTopServices currency={currency} />
      </Flex>
    </Flex>
  );
};

export default DashboardServices;
